import React, { useState } from "react";
import PageHero from "../components/PageHero";
import BankStrip from "../components/BankStrip";

const CHANNELS = [
  {
    key: "seller",
    tab: "Institutional Sellers",
    title: "List with the desk",
    blurb: "For developers, estates and institutional asset holders. Bulk inventory, blueprints, and expected pricing — handled discreetly.",
    fields: [
      { name: "org", label: "Organisation / Estate", type: "text", ph: "Meridian Estates LLP" },
      { name: "assetType", label: "Asset class", type: "select", options: ["Apartments", "Villas", "Houses", "Plots", "Land", "Mixed portfolio"] },
      { name: "count", label: "No. of assets / units", type: "text", ph: "e.g. 12" },
      { name: "value", label: "Indicative portfolio value", type: "text", ph: "₹ in Cr" },
    ],
    cta: "Submit for review",
  },
  {
    key: "buyer",
    tab: "Retail Buyers",
    title: "Tell us what you want",
    blurb: "Post your exact requirement and budget. We match you only to live, title-clean inventory — no cold inventory, no spam.",
    fields: [
      { name: "intent", label: "What are you looking for?", type: "text", ph: "4BR sea-facing villa" },
      { name: "assetType", label: "Asset class", type: "select", options: ["Apartments", "Villas", "Houses", "Plots", "Land"] },
      { name: "budget", label: "Target budget", type: "select", options: ["Under ₹1 Cr", "₹1–3 Cr", "₹3–8 Cr", "₹8–20 Cr", "₹20 Cr+"] },
      { name: "city", label: "Preferred city / locality", type: "text", ph: "Hyderabad · Jubilee Hills" },
    ],
    cta: "Capture my requirement",
  },
  {
    key: "build",
    tab: "Custom Construction",
    title: "Design & build consultation",
    blurb: "From a plot you own — or one we help you acquire — to a finished, turnkey home. Fixed cost, fixed timeline, single accountable house.",
    fields: [
      { name: "stage", label: "Where are you?", type: "select", options: ["I own a plot", "I need a plot + build", "I have a structure to renovate", "Just exploring"] },
      { name: "type", label: "What do you want built?", type: "select", options: ["Villa", "Independent house", "Apartment block", "Commercial", "Other"] },
      { name: "area", label: "Approx. built-up area", type: "text", ph: "e.g. 4,000 sq.ft" },
      { name: "budget", label: "Build budget", type: "select", options: ["Under ₹1 Cr", "₹1–3 Cr", "₹3–8 Cr", "₹8 Cr+"] },
    ],
    cta: "Book a build consult",
  },
];

export default function ContactPage() {
  const [active, setActive] = useState("seller");
  const [sent, setSent] = useState(false);
  const ch = CHANNELS.find((c) => c.key === active);

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <>
      <PageHero
        eyebrow="Contact · Aditi Realty"
        title="One desk for every"
        italic="intent."
        sub="Tell us who you are and we'll route you to the right specialist — institutional sales, retail acquisition, custom construction, or the bank-alliance loan desk."
        tone={["#0B0B0C", "#1a1620"]}
      />

      {/* Multi-channel matrix */}
      <section className="section">
        <div className="shell">
          <div className="grid" style={{ gridTemplateColumns: "0.85fr 1.15fr", gap: "clamp(32px,5vw,72px)", alignItems: "start" }}>
            <div className="reveal">
              <span className="eyebrow">Multi-channel matrix</span>
              <h2 className="h-lg" style={{ marginTop: 16 }}>Pick your lane.</h2>
              <p className="muted" style={{ marginTop: 16 }}>
                Every enquiry reaches a named specialist, not a shared inbox. Choose
                the channel that fits and we'll respond within one business day.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 30 }}>
                {CHANNELS.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => { setActive(c.key); setSent(false); }}
                    style={{
                      textAlign: "left", padding: "18px 20px", borderRadius: 4, cursor: "pointer",
                      border: `1px solid ${active === c.key ? "var(--champagne)" : "var(--alabaster-dim)"}`,
                      background: active === c.key ? "rgba(212,175,55,0.06)" : "#fff",
                      transition: "all 0.3s var(--ease)",
                    }}
                  >
                    <div style={{ fontFamily: "var(--serif)", fontSize: "1.15rem", color: "var(--ink)" }}>{c.tab}</div>
                    <div className="muted" style={{ fontSize: "0.86rem", marginTop: 4 }}>{c.blurb}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Active form */}
            <div className="reveal card" style={{ padding: "clamp(26px,3vw,44px)" }}>
              <span className="eyebrow gold">{ch.tab}</span>
              <h3 className="h-md" style={{ margin: "12px 0 24px" }}>{ch.title}</h3>
              <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Full name</label>
                  <input required type="text" placeholder="Your name" />
                </div>
                <div className="field">
                  <label>Email</label>
                  <input required type="email" placeholder="you@email.com" />
                </div>
                <div className="field">
                  <label>Phone</label>
                  <input required type="tel" placeholder="+91" />
                </div>
                {ch.fields.map((f) => (
                  <div className="field" key={f.name} style={{ gridColumn: f.type === "select" ? "auto" : "auto" }}>
                    <label>{f.label}</label>
                    {f.type === "select" ? (
                      <select required defaultValue="">
                        <option value="" disabled>Select…</option>
                        {f.options.map((o) => <option key={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} placeholder={f.ph} />
                    )}
                  </div>
                ))}
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label>Anything else?</label>
                  <textarea placeholder="Tell us more about what you need…" />
                </div>
                <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <button type="submit" className="btn btn-gold">{ch.cta} <span className="arr">→</span></button>
                  {sent && <span className="badge live"><span className="dot" /> Received — a specialist will reach out</span>}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Financial desk access */}
      <section className="section dark" style={{ paddingTop: "clamp(56px,8vw,104px)" }}>
        <div className="shell">
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "clamp(32px,5vw,72px)", alignItems: "center" }}>
            <div className="reveal">
              <span className="eyebrow gold">Financial desk</span>
              <h2 className="h-lg" style={{ marginTop: 16 }}>Pre-Approved Loan &<br />Bank Alliance Desk</h2>
              <p className="muted" style={{ marginTop: 18 }}>
                Skip the queue. Our financial desk routes your file directly to the
                priority team at our partner banks — pre-negotiated rates, faster
                sanctions, and EMI structuring done before you've even shortlisted.
              </p>
              <div className="flexrow" style={{ marginTop: 28, gap: 14 }}>
                <a href="tel:+910000000000" className="btn btn-gold">Connect to the loan desk <span className="arr">→</span></a>
                <span className="badge"><span className="dot" /> Same-day routing</span>
              </div>
              <div style={{ marginTop: 36 }}>
                <BankStrip dark />
              </div>
            </div>

            <div className="reveal" style={{ background: "var(--obsidian-soft)", border: "1px solid var(--obsidian-line)", borderRadius: 8, padding: "clamp(26px,3vw,40px)" }}>
              <h3 className="h-md" style={{ marginBottom: 20 }}>Reach us directly</h3>
              {[
                ["Acquisitions & sales", "desk@aditirealty.com"],
                ["Design & build studio", "build@aditirealty.com"],
                ["Loan & bank alliances", "finance@aditirealty.com"],
                ["Wealth & arbitrage desk", "wealth@aditirealty.com"],
                ["Phone", "+91 00000 00000"],
              ].map(([k, v]) => (
                <div key={k} className="spaced" style={{ padding: "16px 0", borderBottom: "1px solid var(--obsidian-line)" }}>
                  <span className="muted" style={{ fontSize: "0.82rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>{k}</span>
                  <span className="gold-text" style={{ fontFamily: "var(--serif)", fontSize: "1.05rem" }}>{v}</span>
                </div>
              ))}
              <p className="muted" style={{ fontSize: "0.82rem", marginTop: 18 }}>
                Registered office · Aditi Realty Holdings · Hyderabad · India
              </p>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 860px) {
          .section .grid[style*="0.85fr"] { grid-template-columns: 1fr !important; }
          .section .grid[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
          .card form { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}
